import React, { Component, Fragment } from 'react';
import Header from './Header';
import CSVGenerator from '../utils/json2csv';
import moment from 'moment';
import Table from './Table';
import { getPaymentsReport, getAccountingReport, getBillSubmissionPaymentRequest } from '../utils/PaymentRequestApiUtils';
import { TextField, Button, Snackbar, LinearProgress, Dialog, DialogContent } from '@material-ui/core';
import { getPaymentsReportCSVData, getAccountingReportCSVData, getBillSubmissionReportCSVData } from './Reports';
import { billSubReportStatusMap } from '../constants/paymentStatus';
import { withStyles } from '@material-ui/core/styles';

const styles = theme => ({
  root: {
    width: '100%',
    marginTop: 16,
    overflowX: 'auto',
    fontFamily: 'Inter, sans-serif',
  },
  table: {
    minWidth: 700,
    [theme.breakpoints.down('sm')]: {
      minWidth: '100%',
    },
  },
  container: {
    padding: '1rem',
    [theme.breakpoints.down('sm')]: {
      padding: '0.7rem',
    },
  },
  inputContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  textField: {
    marginRight: '1rem',
    width: '50%',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      marginBottom: '1rem',
    },
  },
  button: {
    marginLeft: '10px',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      marginBottom: '1rem',
      marginLeft: 0,
    },
  },
  tableContainer: {
    overflowX: 'auto',
  },
  timeline: {
    position: 'relative',
    padding: '20px 0',
    margin: '0 auto',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      bottom: 0,
      width: 4,
      backgroundColor: '#ddd',
      left: 'calc(50% - 2px)',
    },
    [theme.breakpoints.down('sm')]: {
      '&::before': {
        left: 'calc(10% - 2px)',
      },
    },
  },
  timelineItem: {
    padding: '10px 20px',
    position: 'relative',
    backgroundColor: 'inherit',
    width: '40%',
    '&:nth-child(odd)': {
      left: '2.55rem',
    },
    '&:nth-child(even)': {
      left: '29.2rem',
    },
    [theme.breakpoints.down('sm')]: {
      scale: '0.8',
      width: '100%',
      '&:nth-child(even)': {
        left: '0.3rem',
      },
      '&:nth-child(odd)': {
        left: '0.3rem',
      },
    },
  },
  timelineContent: {
    padding: '20px 30px',
    backgroundColor: 'white',
    position: 'relative',
    borderRadius: '6px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',

    [theme.breakpoints.down('sm')]: {
      padding: '20px',
    },
  },
  timelineDot: {
    width: 20,
    height: 20,
    backgroundColor: '#fff',
    border: '4px solid #FF9F55',
    borderRadius: '50%',
    position: 'absolute',
    top: 'calc(50% - 10px)',
    right: -25,
    zIndex: 1,
    [theme.breakpoints.down('sm')]: {
      right: 'auto',
      left: -25,
    },
  },
  timelineLeft: {
    '& $timelineDot': {
      left: -25,
    },
  },
});

const BILL_SUBMISSION_REPORT_COLUMNS = [
  {
    Header: 'PR Index',
    key: 'payment_request_id'
  },
  {
    Header: 'Bill Submission Date',
    key: 'bill_submissions_date'
  },
  {
    Header: 'Bill Submission Id',
    key: 'bs_Id',
    width: 180
  },
  {
    Header: 'Bill Submission Requester',
    key: 'bs_requesterEmail'
  },
  {
    Header: 'Type of Transaction',
    key: 'type_of_transaction'
  },
  {
    Header: 'Bill-Sub L1 Approver Date',
    key: 'L1_approver_date'
  },
  {
    Header: 'Bill-Sub L2 Approver Date',
    key: 'L2_approver_date'
  },
  {
    Header: 'Bill-Sub L3 Approver Date',
    key: 'L3_approver_date'
  },
  {
    Header: 'Bill-Sub L4 Approver Date',
    key: 'L4_approver_date'
  },
  {
    Header: 'Vendor name',
    key: 'name'
  },
  {
    Header: 'Payment Request Date',
    key: 'created_at'
  },
  {
    Header: 'Invoice Amount',
    key: 'total_payable_amount'
  },
  {
    Header: 'Total PO Amount',
    key: 'full_invoice_amount_display'
  },
  {
    Header: 'Invoice Date',
    key: 'date_of_invoice'
  },
  {
    Header: 'Invoice Due Date',
    key: 'invoice_due_date'
  },
  {
    Header: 'Invoice Number',
    key: 'invoice_number_link',
    width: 150
  },
  {
    Header: 'Supporting Document',
    key: 'supporting_document_link_link'
  },
  {
    Header: 'Zaps L1 Approver',
    key: 'zaps_approver_l1_name'
  },
  {
    Header: 'Zaps L1 Approver Date',
    key: 'approverId1_at'
  },
  {
    Header: 'Zaps L2 Approver',
    key: 'zaps_approver_l2_name'
  },
  {
    Header: 'Zaps L2 Approver Date',
    key: 'approverId2_at'
  },
  {
    Header: 'Transaction Status',
    key: 'status'
  },
  {
    Header: 'City Finance Remarks',
    key: 'city_finance_remarks'
  },
  {
    Header: 'Zaps L1 Approver Remarks',
    key: 'city_head_remarks'
  },
  {
    Header: 'Zaps L2 Approver Remarks',
    key: 'central_finance_remarks'
  },
  {
    Header: 'Accounting Status',
    key: 'accounting_status'
  },
  {
    Header: 'Accounting Date',
    key: 'accounting_status_date'
  },
  {
    Header: 'Accounted By',
    key: 'accounting_status_by'
  },
  {
    key : 'hold_comment',
    Header: 'Hold Comment'
  },
  {
    key : 'unhold_comment',
    Header: 'Unhold Comment'
  },
  {
    key : 'hold_unhold_by',
    Header: 'Hold/Unhold By'
  },
  {
    key : 'hold_unhold_date',
    Header: 'Hold/Unhold Date'
  },
  {
    Header: 'Payment Date',
    key: 'transaction_date'
  },
  {
    Header: 'UTR Number',
    key: 'transaction_id'
  },
  {
    Header: 'City',
    key: 'city'
  },
  {
    Header: 'Deleted At',
    key: 'deleted_at'
  },
  {
    Header: 'Deleted By',
    key: 'deleted_by'
  },
  {
    Header: 'Actions',
    key: 'actions',
    width: 180
  }
]

class PaymentRequestPreview extends Component {
  constructor() {
    super();
    this.state = {
      data: [],
      inProgress: false,
      snackMessage: "",
      snackOpen: false,
      validationsDialogOpen: false,
      validationErrors: [],
      dialogBoxText: "",
      showDownloadButton: false,
      paymentsReportColumns: [],
      accountingReportColumns: [],
      billSubmissionReportColumns: [],
      billSubmissionIds: '',
      isMobileView: window.innerWidth <= 600,
    }
    this.handleChange = this.handleChange.bind(this);
    this.submitFilter = this.submitFilter.bind(this);
    this.clearFilter = this.clearFilter.bind(this);
    this.generateCSV = this.generateCSV.bind(this);
    this.handleCloseSnack = this.handleCloseSnack.bind(this);
    this.handleFetchData = this.handleFetchData.bind(this);
    this.handleBillSubmissionIdsChange = this.handleBillSubmissionIdsChange.bind(this);
    this.handleViewTimeline = this.handleViewTimeline.bind(this);
    this.handleResize = this.handleResize.bind(this);
  }

  componentWillMount() {
    this.setState({
      showDownloadButton: true,
      billSubmissionReportColumns: BILL_SUBMISSION_REPORT_COLUMNS
    })
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  handleResize() {
    this.setState({ isMobileView: window.innerWidth <= 600 });
  }

  handleChange = (props) => (event, index, value) => {
    this.setState({ [props]: event.target.value, [props + 'Err']: false, [props + 'ErrText']: '' })
  }

  handleBillSubmissionIdsChange(event) {
    this.setState({ billSubmissionIds: event.target.value });
  }

  async submitFilter() {
    // Validate billSubmissionIds input
    if (!this.state.billSubmissionIds) {
      this.setState({ snackOpen: true, snackMessage: 'Please enter Bill Submission IDs' });
      return;
    }

    // Remove leading and trailing spaces from billSubmissionIds
    const trimmedIds = this.state.billSubmissionIds ? this.state.billSubmissionIds.split(',').map(id => id.trim()) : [];

    // Fetch data using the new API call
    this.handleFetchData({ bill_submission_ids: trimmedIds });
  }

  handleFetchData(filters = {}) {
    this.setState({ inProgress: true }, () => {
      getBillSubmissionPaymentRequest(filters).then(response => {
        if (response) {
          if (response.status === 'success') {
            let data = response.data.map(data => {
              return {
                ...data,
                transaction_date: (data.transaction_date) ? moment(data.transaction_date).format('DD-MM-YYYY') : '-',
                L1_approver_date: (data.L1_approver_date) ? moment(data.L1_approver_date).format('DD-MM-YYYY') : '-',
                L2_approver_date: (data.L2_approver_date) ? moment(data.L2_approver_date).format('DD-MM-YYYY') : '-',
                L3_approver_date: (data.L3_approver_date) ? moment(data.L3_approver_date).format('DD-MM-YYYY') : '-',
                L4_approver_date: (data.L4_approver_date) ? moment(data.L4_approver_date).format('DD-MM-YYYY') : '-',
                zaps_approver_l1_name: (data.zaps_approver_l1_name) ? data.zaps_approver_l1_name : '-',
                approverId1_at: (data.approverId1_at) ? moment(data.approverId1_at).format('DD-MM-YYYY hh:mm A') : '-',
                zaps_approver_l2_name: (data.zaps_approver_l2_name) ? data.zaps_approver_l2_name : '-',
                approverId2_at: (data.approverId2_at) ? moment(data.approverId2_at).format('DD-MM-YYYY hh:mm A ') : '-',
                bill_submissions_date: (data.bill_submissions_date) ? moment(data.bill_submissions_date).format('DD-MM-YYYY') : '-',
                total_payable_amount: (data.total_payable_amount) ? data.total_payable_amount : '-',
                full_invoice_amount_display: (data.partial_payment_percentage && data.full_invoice_amount) ? data.full_invoice_amount : '-',
                date_of_invoice: (data.date_of_invoice) ? moment(data.date_of_invoice).format('DD-MM-YYYY') : '-',
                status: billSubReportStatusMap[data.status].value,
                invoice_due_date: (data.invoice_due_date) ? moment(data.invoice_due_date).format('DD-MM-YYYY') : '-',
                created_at: (data.created_at) ? moment(data.created_at).format('DD-MM-YYYY hh:mm A') : '-',
                type_of_transaction: (data.type_of_transaction) ? data.type_of_transaction : '-',
                name: (data.name) ? data.name : '-',
                transaction_id: (data.transaction_id) ? data.transaction_id : '-',
                bs_Id: (data.bs_Id) ? data.bs_Id : '-',
                bs_requesterEmail: (data.bs_requesterEmail) ? data.bs_requesterEmail : '-',
                city: (data.CITY) ? data.CITY : '-',
                deleted_at: (data.deleted_at) ? moment(data.deleted_at).format('DD-MM-YYYY hh:mm A') : '-',
                deleted_by: (data.deleted_by) ? data.deleted_by : '-',
                invoice_number: (data.invoice_number) ? data.invoice_number : '-',
                city_finance_remarks: (data.city_finance_remarks) ? data.city_finance_remarks : '-',
                city_head_remarks: (data.city_head_remarks) ? data.city_head_remarks : '-',
                central_finance_remarks: (data.central_finance_remarks) ? data.central_finance_remarks : '-',
                accounting_status: data.accounting_status === 'accounted' ? 'Accounted' : 'Unaccounted',
                accounting_status_date: data.accounting_status_date ? moment(data.accounting_status_date).format('DD-MM-YYYY hh:mm A') : '-',
                hold_unhold_date: data.hold_unhold_date ? moment(data.hold_unhold_date).format('DD-MM-YYYY hh:mm A') : '-',
                accounting_status_by: data.accounting_status_by ? data.accounting_status_by : '-',
                hold_comment: data.hold_comment ? data.hold_comment : '-',
                unhold_comment: data.unhold_comment ? data.unhold_comment : '-',
                hold_unhold_by: data.hold_unhold_by ? data.hold_unhold_by : '-',
                invoice_number_link: (data.invoice_number) ? (
                  <Fragment>
                    <a href={data.invoice_soft_copy_link} target="_blank" rel="noopener noreferrer">{data.invoice_number}</a>
                  </Fragment>
                ) : '-',
                supporting_document_link_link: (data.supporting_document_link) ? (
                  <Fragment>
                    <a href={data.supporting_document_link} target="_blank" rel="noopener noreferrer">{data.supporting_document_link}</a>
                  </Fragment>
                ) : '-',
                actions: (
                  <Button
                    variant="contained"
                    size="small"
                    color="primary"
                    onClick={() => this.handleViewTimeline(data)}
                  >
                    View Timeline
                  </Button>
                )
              }
            })
            this.setState({ data, inProgress: false })
          }
          else {
            this.setState({ snackMessage: response.message, snackOpen: true, inProgress: false })
          }
        }
      })
    })
  }


  handleCloseSnack() {
    this.setState({ snackOpen: false })
  }

  clearFilter() {
    this.setState({
      billSubmissionIds: '',
      data: []
    });
  }

  async generateCSV() {
    let csvData = await getBillSubmissionReportCSVData(this.state.data);
    CSVGenerator(csvData.headers, csvData.data, 'ZAPS_Payment_Request_Report')
  }

  generateAllCSVs() {

    // Download Bill Submission Report
    this.generateCSV();

    // Download Payments Report
    let paymentsReportCSVData = {};
    getPaymentsReport({ from_date: this.state.fromDate, to_date: this.state.toDate }).then(response => {
      if (response) {
        if (response.status === 'success') {
          let paymentsReportData = response.data.map(data => {
            return {
              ...data,
              transaction_date: (data.transaction_date) ? moment(data.transaction_date).format('DD-MM-YYYY') : data.transaction_date,
            }
          })
          this.setState({ paymentsReportData });
        }
        else {
          this.setState({ snackMessage: response.message, snackOpen: true, inProgress: false })
        }
      }
    }).then(async () => {
      paymentsReportCSVData = await getPaymentsReportCSVData(this.state.paymentsReportData);
    }).then(() => CSVGenerator(paymentsReportCSVData.headers, paymentsReportCSVData.data, paymentsReportCSVData.filename));

    // Download Accounting Report
    let accountingReportCSVData = {};
    getAccountingReport({ from_date: this.state.fromDate, to_date: this.state.toDate }).then(response => {
      if (response) {
        if (response.status === 'success') {
          let accountingReportData = response.data.map(data => {
            return {
              ...data,
              transaction_date: (data.transaction_date) ? moment(data.transaction_date).format('DD-MM-YYYY') : data.transaction_date,
              payment_request_date: (data.payment_request_date) ? moment(data.payment_request_date).format('DD-MM-YYYY') : data.payment_request_date,
            }
          })
          this.setState({ accountingReportData });
        }
        else {
          this.setState({ snackMessage: response.message, snackOpen: true, inProgress: false })
        }
      }
    }).then(async () => {
      accountingReportCSVData = await getAccountingReportCSVData(this.state.accountingReportData);
    }).then(() => CSVGenerator(accountingReportCSVData.headers, accountingReportCSVData.data, accountingReportCSVData.filename));
  }

  handleViewTimeline(data) {
    window.open(`/payment/payment-request-preview/timeline?id=${data.bs_Id}`, '_blank');
  }
 
  render() {
    const { classes } = this.props;
    const { isMobileView } = this.state;
    return (
      <Fragment>
        <Header />
        <div className={classes.container}>
          <div className={classes.inputContainer}>
            <div 
              style={{ 
                width: '100%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                flexWrap: 'wrap' 
              }}
            >
              <div style={{ flexGrow: 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                Enter Bill Submission IDs:&nbsp;&nbsp;&nbsp;&nbsp;
                <TextField
                  id="billSubmissionIds"
                  type="text"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  value={this.state.billSubmissionIds}
                  onChange={this.handleBillSubmissionIdsChange}
                  className={classes.textField}
                  margin='normal'
                />
                <Button
                  variant="contained"
                  size="large"
                  color="primary"
                  disabled={this.state.billSubmissionIds === ''}
                  onClick={this.submitFilter.bind(this)}
                  className={classes.button}
                >
                  Submit Filter
                </Button>
              </div>
              {!isMobileView && (
                <Button 
                  variant="contained" 
                  size="large" 
                  color="primary" 
                  disabled={this.state.data.length === 0} 
                  onClick={this.generateCSV.bind(this)}
                  className={classes.button}
                  style={{ flexShrink: 0, marginTop: '8px' }}
                >
                  Download CSV
                </Button>
              )}
            </div>
          </div>
          <div className={classes.tableContainer}>
            <Table
              columns={this.state.billSubmissionReportColumns}
              data={this.state.data}
              margin='0'
            />
          </div>
          {isMobileView && (
            <Button 
              variant="contained" 
              size="large" 
              color="primary" 
              disabled={this.state.data.length === 0} 
              onClick={this.generateCSV.bind(this)}
              className={classes.button}
              style={{ width: '100%', flexShrink: 0 }}
            >
              Download CSV
            </Button>
          )}
        </div>
        <Snackbar
          open={this.state.snackOpen}
          message={this.state.snackMessage}
          autoHideDuration={2500}
          onClose={this.handleCloseSnack.bind(this)}
        />
      </Fragment>
    )
  }
}

export default withStyles(styles)(PaymentRequestPreview);



// WEBPACK FOOTER //
// ./src/components/PaymentRequestPreview.js